import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Users,
  TrendingUp,
  Wallet,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Download,
  ArrowLeft,
  RefreshCw,
  BadgeCheck,
  AlertTriangle,
  Briefcase,
  Bell,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAnalytics, useMyWalletAndAssets, useProfile } from "@/hooks";
import { formatCurrency } from "@/utils";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";
import { Badge } from "@/components/ui/badge";
import { useAppSelector } from "@/store/hooks";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";

const userGrowthData = [
  { month: "Jan", users: 12000, newUsers: 1200 },
  { month: "Feb", users: 13200, newUsers: 1400 },
  { month: "Mar", users: 14800, newUsers: 1600 },
  { month: "Apr", users: 16200, newUsers: 1800 },
  { month: "May", users: 18000, newUsers: 2100 },
  { month: "Jun", users: 20500, newUsers: 2500 },
  { month: "Jul", users: 23200, newUsers: 2800 },
  { month: "Aug", users: 25800, newUsers: 3100 },
  { month: "Sep", users: 28500, newUsers: 3400 },
  { month: "Oct", users: 31200, newUsers: 3800 },
  { month: "Nov", users: 34200, newUsers: 4200 },
  { month: "Dec", users: 38500, newUsers: 4800 },
];

const volumeDistributionData = [
  { name: "BTC", value: 35, color: "#F0B90B" },
  { name: "ETH", value: 28, color: "#627EEA" },
  { name: "USDT", value: 22, color: "#26A17B" },
  { name: "NGNT", value: 10, color: "#008751" },
  { name: "Others", value: 5, color: "#94A3B8" },
];

const tradingVolumeData = [
  { day: "Mon", spot: 2400, p2p: 1200, forex: 800 },
  { day: "Tue", spot: 2800, p2p: 1400, forex: 900 },
  { day: "Wed", spot: 3200, p2p: 1600, forex: 1100 },
  { day: "Thu", spot: 3600, p2p: 1800, forex: 1200 },
  { day: "Fri", spot: 4200, p2p: 2100, forex: 1400 },
  { day: "Sat", spot: 3800, p2p: 1900, forex: 1300 },
  { day: "Sun", spot: 3400, p2p: 1700, forex: 1100 },
];

const kycData = [
  { month: "Jan", verified: 800, pending: 200, rejected: 50 },
  { month: "Feb", verified: 950, pending: 180, rejected: 40 },
  { month: "Mar", verified: 1100, pending: 220, rejected: 60 },
  { month: "Apr", verified: 1250, pending: 150, rejected: 45 },
  { month: "May", verified: 1400, pending: 180, rejected: 55 },
  { month: "Jun", verified: 1600, pending: 200, rejected: 40 },
];

const geographicData = [
  { country: "Nigeria", users: 12450, percentage: 32 },
  { country: "Ghana", users: 8900, percentage: 23 },
  { country: "Kenya", users: 6700, percentage: 17 },
  { country: "South Africa", users: 5400, percentage: 14 },
  { country: "Others", users: 5050, percentage: 14 },
];

const mockUserAnalytics = {
  user: {
    role: "USER",
    status: "ACTIVE",
    kycLevel: 2,
    kycStatus: "UNDER_REVIEW",
    advertiserTier: "VERIFIED",
    reputationScore: 78,
    completedTrades: 42,
    disputeRate: 3.2,
  },

  wallet: {
    balance: 12450.32,
    inflow: 40200.12,
    outflow: 27749.8,
    reserved: 1250.0,
    feeRevenue: 340.22,
  },

  trading: {
    totalVolume: 98500,
    completedTrades: 42,
    pendingTrades: 3,
    disputes: 2,
    escrowLocked: 6,
  },

  security: {
    activeSessions: 2,
    devices: 3,
    failedLogins: 4,
    loginRiskScore: 22,
  },

  support: {
    openTickets: 1,
    resolvedTickets: 8,
    totalTickets: 9,
  },

  referrals: {
    totalReferrals: 12,
    rewardsEarned: 48.5,
  },

  activity: {
    swaps: 5,
    adsCreated: 3,
    notifications: 21,
  },
};

const tokenColors: Record<string, string> = {
  BTC: "#F7931A",
  ETH: "#627EEA",
  tUSD: "#26A17B",
  tNGN: "#F3BA2F",
  SOL: "#9945FF",
  XRP: "#23292F",
  ADA: "#0033AD",
  DOGE: "#C2A633",
  MATIC: "#8247E5",
  TRX: "#EF0027",
  LTC: "#345D9D",
  DOT: "#E6007A",
  AVAX: "#E84142",
  default: "#8884D8",
};

function StatCard({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string;
  value: string | number;
  icon: any;
  description?: string;
}) {
  return (
    <Card className="rounded-2xl border bg-background shadow-sm">
      <CardContent className="p-4 flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="text-xl font-semibold tracking-tight">{value}</p>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>

        <div className="p-2 rounded-xl bg-muted">
          <Icon className="w-4 h-4 text-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

const AdminAnalytics = () => {
  const [timeRange, setTimeRange] = useState("30d");

  const navigate = useNavigate();

  const { data: analytics, isLoading, isError, refetch } = useAnalytics("30");

  const [isRefreshing, setIsRefreshing] = useState(false);

  const data = useMemo(() => mockUserAnalytics, []);

  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id;

  const {
    data: myWallet,
    isLoading: isLoadingWallet,
    isError: isErrorWallet,
    refetch: refetchWallet,
  } = useMyWalletAndAssets(userId!);

  const riskLevel =
    data.security.loginRiskScore < 30
      ? "LOW"
      : data.security.loginRiskScore < 70
        ? "MEDIUM"
        : "HIGH";

  const portfolioHistory = myWallet?.analytics?.portfolioHistory || [];

  const assetAllocation = myWallet?.analytics?.assetAllocation || [];

  const {
    data: profile,
    // isLoading,
    // isError,
    // refetch,
  } = useProfile(userId as string);

  console.log("ANALYTICS", analytics);

  const handleExport = () => {
    toast("Export Started", {
      description: "Analytics report is being generated...",
    });
    setTimeout(() => {
      toast("Export Complete", {
        description: "Analytics report downloaded successfully.",
      });
    }, 1500);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch();
    // refetchWallet();
    setTimeout(() => {
      setIsRefreshing(false);
      toast.success("Analytics refreshed", {
        description: "Your analytics data has been updated.",
      });
    }, 1000);
  };

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
        }}
      />
    );

  if (isLoading) return <Loader type="bounce" />;

  return (
    <div className="min-h-screen bg-background lg:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Button
          className="hidden  lg:flex"
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Analytics Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Platform performance metrics and insights
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-card text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="nex-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <span
                className={`text-xs flex items-center gap-1 ${true ? "text-emerald-500" : "text-red-500"}`}
              >
                {true ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
                12.5%
              </span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {analytics?.stats?.totalUsers}
            </p>
            <p className="text-xs text-muted-foreground">Total Users</p>
          </div>
          <div className="nex-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <span
                className={`text-xs flex items-center gap-1 ${true ? "text-emerald-500" : "text-red-500"}`}
              >
                {true ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
                7.5%
              </span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency(analytics?.stats?.dailyVolume)}
            </p>
            <p className="text-xs text-muted-foreground">Daily Volume</p>
          </div>
          <div className="nex-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-primary" />
              </div>
              <span
                className={`text-xs flex items-center gap-1 ${true ? "text-emerald-500" : "text-red-500"}`}
              >
                {true ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
                9.11%
              </span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency(analytics?.stats?.revenue)}
            </p>
            <p className="text-xs text-muted-foreground">Total Revenue</p>
          </div>
          <div className="nex-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Activity className="w-5 h-5 text-primary" />
              </div>
              <span
                className={`text-xs flex items-center gap-1 ${true ? "text-emerald-500" : "text-red-500"}`}
              >
                {false ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
                15.8%
              </span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {analytics?.stats?.activeSessions}
            </p>
            <p className="text-xs text-muted-foreground">Active Sessions</p>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* User Growth Chart */}
          <div className="nex-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-foreground text-lg">
                  User Growth
                </h3>
                <p className="text-sm text-muted-foreground">
                  Monthly user acquisition
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>Last 12 months</span>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={userGrowthData}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F0B90B" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F0B90B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="month"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    itemStyle={{ color: "hsl(var(--foreground))" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="users"
                    stroke="#F0B90B"
                    fillOpacity={1}
                    fill="url(#colorUsers)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Volume Distribution Chart */}
          <div className="nex-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-foreground text-lg">
                  Volume Distribution
                </h3>
                <p className="text-sm text-muted-foreground">
                  Trading volume by asset
                </p>
              </div>
            </div>
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics?.tradingVolume}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {analytics?.tradingVolume.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        // fill={entry.color}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    itemStyle={{ color: "hsl(var(--foreground))" }}
                    formatter={(value: number) => [`${value}%`, "Share"]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Trading Volume Chart */}
          <div className="nex-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-foreground text-lg">
                  Trading Volume
                </h3>
                <p className="text-sm text-muted-foreground">
                  Daily volume by trading type
                </p>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tradingVolumeData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    itemStyle={{ color: "hsl(var(--foreground))" }}
                    formatter={(value: number) => [
                      `$${value.toLocaleString()}`,
                      "Volume",
                    ]}
                  />
                  <Legend />
                  <Bar
                    dataKey="spot"
                    name="Spot Trading"
                    fill="#F0B90B"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="p2p"
                    name="P2P Trading"
                    fill="#627EEA"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="forex"
                    name="Forex"
                    fill="#26A17B"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* KYC Verification Chart */}
          <div className="nex-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-foreground text-lg">
                  KYC Verification
                </h3>
                <p className="text-sm text-muted-foreground">
                  Monthly KYC status breakdown
                </p>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={kycData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="month"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    itemStyle={{ color: "hsl(var(--foreground))" }}
                  />
                  <Legend />
                  <Bar
                    dataKey="verified"
                    name="Verified"
                    fill="#10B981"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="pending"
                    name="Pending"
                    fill="#F0B90B"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="rejected"
                    name="Rejected"
                    fill="#EF4444"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Geographic Distribution */}
        <div className="nex-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-foreground text-lg">
                Geographic Distribution
              </h3>
              <p className="text-sm text-muted-foreground">Users by country</p>
            </div>
          </div>
          <div className="space-y-4">
            {analytics?.geography?.map((item, index) => (
              <div key={item.country} className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-foreground font-medium">
                      {item.country}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      {item?.users?.toLocaleString()} users ({item.percentage}%)
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-500"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="min-h-screen bg-background px-6 py-6 space-y-6">
        <div className="hidden sm:flex items-center justify-between gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={cn("h-4 w-4", isRefreshing && "animate-spin")}
            />
          </Button>
        </div>
        {/* ================= HEADER ================= */}
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              User Analytics
            </h1>
            <p className="text-sm text-muted-foreground">
              Behavioral, financial, security, and trading intelligence overview
            </p>
          </div>

          <Badge variant="secondary" className="rounded-full">
            {data.user.status} • KYC L{profile?.kycLevel}
          </Badge>
        </div>

        {/* ================= KPI GRID ================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Wallet Balance"
            value={`$${data.wallet.balance.toLocaleString()}`}
            icon={Wallet}
            description="Available funds"
          />
          <StatCard
            title="Trading Volume"
            value={`$${data.trading.totalVolume.toLocaleString()}`}
            icon={TrendingUp}
            description="Lifetime activity"
          />
          <StatCard
            title="Reputation Score"
            value={data.user.reputationScore}
            icon={BadgeCheck}
            description="Trust index"
          />
          <StatCard
            title="Dispute Rate"
            value={`${data.user.disputeRate}%`}
            icon={AlertTriangle}
            description="Risk indicator"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Portfolio Chart */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Portfolio Performance</CardTitle>
                <CardDescription>7-day value history</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                  <RefreshCw
                    className={cn(
                      "h-4 w-4 mr-2",
                      isRefreshing && "animate-spin",
                    )}
                  />
                  Refresh
                </Button>
                <Badge
                  variant="outline"
                  className="text-xs cursor-pointer hover:bg-violet-500/10"
                >
                  7D
                </Badge>
                <Badge variant="secondary" className="text-xs cursor-pointer">
                  30D
                </Badge>
                <Badge
                  variant="outline"
                  className="text-xs cursor-pointer hover:bg-violet-500/10"
                >
                  90D
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={portfolioHistory}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    opacity={0.2}
                  />
                  <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={12}
                    tickFormatter={(v) => `$${v / 1000}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937",
                      border: "none",
                      borderRadius: "8px",
                    }}
                    formatter={(value: number) => [
                      `$${value.toLocaleString()}`,
                      "Portfolio Value",
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#8b5cf6"
                    fillOpacity={1}
                    fill="url(#colorValue)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Asset Allocation */}
          <Card
            className="cursor-pointer hover:shadow-lg transition-all"
            onClick={() => navigate("/dashboard/wallet")}
          >
            <CardHeader>
              <CardTitle className="text-lg">Asset Allocation</CardTitle>
              <CardDescription>By portfolio value</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={assetAllocation}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {assetAllocation.map((entry: any, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={tokenColors[entry.name] || tokenColors.default}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937",
                      border: "none",
                      borderRadius: "8px",
                    }}
                    formatter={(value: number, name: string, props: any) => [
                      `${value}% ($${props.payload.amount.toLocaleString()})`,
                      props.payload.name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-4">
                {assetAllocation.map((asset: any) => (
                  <div
                    key={asset.name}
                    className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-1 rounded"
                    onClick={() => navigate("/dashboard/wallet")}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{
                          backgroundColor:
                            tokenColors[asset.name] || tokenColors.default,
                        }}
                      />
                      <span className="text-sm">{asset.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium">
                        {asset.value}%
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        ${asset.amount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ================= FINANCIAL + TRADING ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="rounded-2xl border bg-background shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Wallet Flow</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Row
                label="Inflows"
                value={`$${data.wallet.inflow.toLocaleString()}`}
              />
              <Row
                label="Outflows"
                value={`$${data.wallet.outflow.toLocaleString()}`}
              />
              <Row
                label="Reserved"
                value={`$${data.wallet.reserved.toLocaleString()}`}
              />
              <Row
                label="Fee Revenue"
                value={`$${data.wallet.feeRevenue.toLocaleString()}`}
                strong
              />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border bg-background shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Trading Health</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Row
                label="Completed Trades"
                value={data.trading.completedTrades}
              />
              <Row label="Pending Trades" value={data.trading.pendingTrades} />
              <Row label="Escrow Locked" value={data.trading.escrowLocked} />
              <Row label="Disputes" value={data.trading.disputes} danger />
            </CardContent>
          </Card>
        </div>

        {/* ================= SECURITY ================= */}
        <Card className="rounded-2xl border bg-background shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Security Overview</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <MiniStat label="Sessions" value={data.security.activeSessions} />
              <MiniStat label="Devices" value={data.security.devices} />
              <MiniStat
                label="Failed Logins"
                value={data.security.failedLogins}
              />
            </div>

            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Risk Score</span>
                <span className="font-medium">
                  {data.security.loginRiskScore}/100 ({riskLevel})
                </span>
              </div>
              <Progress value={data.security.loginRiskScore} />
            </div>
          </CardContent>
        </Card>

        {/* ================= SUPPORT + REFERRALS ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="rounded-2xl border bg-background shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Support Activity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Row label="Total Tickets" value={data.support.totalTickets} />
              <Row label="Open Tickets" value={data.support.openTickets} />
              <Row label="Resolved" value={data.support.resolvedTickets} />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border bg-background shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Referrals</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Row
                label="Total Referrals"
                value={data.referrals.totalReferrals}
              />
              <Row
                label="Rewards Earned"
                value={`$${data.referrals.rewardsEarned}`}
              />
            </CardContent>
          </Card>
        </div>

        {/* ================= ACTIVITY ================= */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            title="Swaps"
            value={data.activity.swaps}
            icon={RefreshCw}
          />
          <StatCard
            title="Ads Created"
            value={data.activity.adsCreated}
            icon={Briefcase}
          />
          <StatCard
            title="Notifications"
            value={data.activity.notifications}
            icon={Bell}
          />
        </div>
      </div>
    </div>
  );
};

function Row({
  label,
  value,
  strong,
  danger,
}: {
  label: string;
  value: any;
  strong?: boolean;
  danger?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`font-medium ${
          danger ? "text-red-500" : strong ? "text-foreground" : ""
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * ============================
 * MINI STAT
 * ============================
 */
function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="p-3 rounded-xl border bg-muted/40">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

export default AdminAnalytics;
